import { differenceInMinutes, parseISO } from 'date-fns';
import type { IsoDate, IsoDateTime } from '../entities/entities';
import type { TimerEntity } from '../entities/timer';
import type { TimeEntry, WorkEntity2 } from '../entities/work';

export function createClosedTimeEntry(
  startTime: string,
  stopTime: string | Date = new Date()
): TimeEntry {
  const startDate = parseISO(startTime);
  const stopDate = typeof stopTime === 'string' ? parseISO(stopTime) : stopTime;

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(stopDate.getTime())) {
    throw new Error('Invalid time entry range');
  }

  return {
    startTime,
    totalMinutes: Math.max(0, differenceInMinutes(stopDate, startDate)),
  };
}

export function timeEntriesOverlap(left: TimeEntry, right: TimeEntry): boolean {
  const leftStart = parseISO(left.startTime).getTime();
  const rightStart = parseISO(right.startTime).getTime();
  const leftEnd = leftStart + left.totalMinutes * 60_000;
  const rightEnd = rightStart + right.totalMinutes * 60_000;

  if (
    !Number.isFinite(leftStart) ||
    !Number.isFinite(rightStart) ||
    !Number.isFinite(left.totalMinutes) ||
    !Number.isFinite(right.totalMinutes) ||
    left.totalMinutes <= 0 ||
    right.totalMinutes <= 0 ||
    !Number.isFinite(leftEnd) ||
    !Number.isFinite(rightEnd)
  ) {
    return false;
  }

  return leftStart < rightEnd && rightStart < leftEnd;
}

export function timerTimeEntries(
  timer: TimerEntity,
  now = new Date()
): Record<string, TimeEntry> {
  const timeEntries: Record<string, TimeEntry> =
    timer.workItemState === 'embedded' ? { ...(timer.work.timeEntries ?? {}) } : {};
  if (timer.timerState !== 'active') {
    return timeEntries;
  }

  const start = parseISO(timer.startTime);
  if (!Number.isNaN(start.getTime())) {
    timeEntries.__running = {
      startTime: timer.startTime,
      totalMinutes: Math.max(1, Math.ceil((now.getTime() - start.getTime()) / 60_000)),
    };
  }

  return timeEntries;
}

export function buildTimeEntryStartTime(date: IsoDate, startMinutes: number): IsoDateTime {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const day = Number(date.slice(8, 10));
  const hours = Math.floor(startMinutes / 60);
  const minutes = startMinutes % 60;

  return new Date(year, month - 1, day, hours, minutes, 0, 0).toISOString();
}

export function sumTimeEntryMinutes(
  timeEntries: Record<string, TimeEntry> | null | undefined
): number {
  if (!timeEntries) return 0;

  return Object.values(timeEntries).reduce((total, entry) => total + (entry.totalMinutes || 0), 0);
}

export function appendTimeEntry(
  timeEntries: Record<string, TimeEntry> | null | undefined,
  entryId: string,
  entry: TimeEntry
): Record<string, TimeEntry> {
  return {
    ...(timeEntries ?? {}),
    [entryId]: entry,
  };
}

export function appendStoppedTimerMinutes(
  existingTimeEntries: WorkEntity2['timeEntries'] | undefined,
  existingWorkMinutes: WorkEntity2['workMinutes'] | undefined,
  stoppedEntry: TimeEntry
): number {
  const baseMinutes = existingWorkMinutes ?? sumTimeEntryMinutes(existingTimeEntries);
  return baseMinutes + (stoppedEntry.totalMinutes || 0);
}

export function shouldAppendStoppedTimerEntry(
  existingTimeEntries: WorkEntity2['timeEntries'] | undefined,
  existingWorkMinutes: WorkEntity2['workMinutes'] | undefined
): boolean {
  return (
    (!!existingTimeEntries && Object.keys(existingTimeEntries).length > 0) ||
    (existingWorkMinutes ?? 0) <= 0
  );
}

type EmbeddedTimerStopUpdate<TStartTimeDeleteField> = {
  timerState: 'inactive';
  'work.workMinutes': number;
  startTime: TStartTimeDeleteField;
} & Record<string, 'inactive' | number | TimeEntry | TStartTimeDeleteField>;

type LinkedTimerStopWorkUpdate = {
  workMinutes: number;
} & Record<string, number | TimeEntry>;

export function buildEmbeddedTimerStopUpdate<TStartTimeDeleteField>(
  existingTimeEntries: WorkEntity2['timeEntries'] | undefined,
  existingWorkMinutes: WorkEntity2['workMinutes'] | undefined,
  timeEntryId: string,
  timeEntry: TimeEntry,
  startTimeDeleteField: TStartTimeDeleteField
): EmbeddedTimerStopUpdate<TStartTimeDeleteField> {
  const update = {
    timerState: 'inactive' as const,
    'work.workMinutes': appendStoppedTimerMinutes(
      existingTimeEntries,
      existingWorkMinutes,
      timeEntry
    ),
    startTime: startTimeDeleteField,
  };

  return (
    shouldAppendStoppedTimerEntry(existingTimeEntries, existingWorkMinutes)
      ? { ...update, [`work.timeEntries.${timeEntryId}`]: timeEntry }
      : update
  ) as EmbeddedTimerStopUpdate<TStartTimeDeleteField>;
}

export function buildLinkedTimerStopWorkUpdate(
  existingTimeEntries: WorkEntity2['timeEntries'] | undefined,
  existingWorkMinutes: WorkEntity2['workMinutes'] | undefined,
  timeEntryId: string,
  timeEntry: TimeEntry
): LinkedTimerStopWorkUpdate {
  const update = {
    workMinutes: appendStoppedTimerMinutes(existingTimeEntries, existingWorkMinutes, timeEntry),
  };

  return (
    shouldAppendStoppedTimerEntry(existingTimeEntries, existingWorkMinutes)
      ? { ...update, [`timeEntries.${timeEntryId}`]: timeEntry }
      : update
  ) as LinkedTimerStopWorkUpdate;
}

export function workMinutesAfterTimeEntryUpdate(
  existingWorkMinutes: number,
  existingEntry: TimeEntry,
  nextTotalMinutes: number
): number {
  return Math.max(0, existingWorkMinutes - (existingEntry.totalMinutes || 0) + nextTotalMinutes);
}

export function workMinutesAfterTimeEntryDelete(
  existingWorkMinutes: number,
  deletedEntry: TimeEntry
): number {
  return Math.max(0, existingWorkMinutes - (deletedEntry.totalMinutes || 0));
}
